import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import './Legal.css';
import AppHeader from '../../components/AppHeader';
import Footer from '../../components/Footer/Footer';

function TermsOfService() {
  const navigate = useNavigate();
  const lastUpdated = 'December 1, 2025';

  return (
    <div className="legal-page">
      <AppHeader showActions={false} />

      <div className="page-back-nav">
        <button
          className="back-to-home-btn"
          onClick={() => navigate('/loyalty-directory')}
          aria-label="Back to Home"
        >
          <FaArrowLeft />
          <span>Back to Home</span>
        </button>
      </div>

      <div className="legal-container">
        <header className="legal-header">
          <h1>Terms of Service</h1>
          <p className="legal-last-updated">Last Updated: {lastUpdated}</p>
        </header>

        <div className="legal-content">
          <section className="legal-section">
            <h2>1. Agreement to Terms</h2>
            <p>
              These Terms of Service ("Terms") constitute a legally binding agreement between you and Block Club ("Company," "we," "us," or "our") concerning your access to and use of the Block Club loyalty program platform and related services (collectively, the "Services").
            </p>
            <p>
              By accessing or using our Services, you agree that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you must not access or use the Services.
            </p>
            <p>
              We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms and changing the "Last Updated" date. Your continued use of the Services after such changes constitutes your acceptance of the new Terms.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Eligibility</h2>
            <p>
              To use our Services, you must:
            </p>
            <ul>
              <li>Be at least 18 years of age</li>
              <li>Have the legal capacity to enter into binding contracts</li>
              <li>Not be barred from using the Services under applicable law</li>
              <li>Be an authorized member of a participating organization or company, if applicable</li>
              <li>Provide accurate, current, and complete information during registration</li>
            </ul>
            <p>
              By using the Services, you represent and warrant that you meet all of these eligibility requirements.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. Account Registration and Security</h2>

            <h3>3.1 Account Creation</h3>
            <p>
              To access certain features of the Services, you must create an account. You agree to:
            </p>
            <ul>
              <li>Provide accurate, complete, and current information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
              <li>Accept responsibility for all activities that occur under your account</li>
            </ul>

            <h3>3.2 Account Termination</h3>
            <p>
              We reserve the right to suspend or terminate your account at any time, with or without notice, for any reason, including if we believe you have violated these Terms. You may terminate your account at any time by contacting us at support@blockclub.com.
            </p>
          </section>

          <section className="legal-section">
            <h2>4. Use of Services</h2>

            <h3>4.1 License Grant</h3>
            <p>
              Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Services for your personal, non-commercial use.
            </p>

            <h3>4.2 Restrictions</h3>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Services for any illegal purpose or in violation of any laws</li>
              <li>Access or attempt to access the Services by any means other than through the interface provided</li>
              <li>Circumvent, disable, or interfere with security-related features</li>
              <li>Reverse engineer, decompile, or disassemble any aspect of the Services</li>
              <li>Use automated systems (bots, scrapers, etc.) to access the Services</li>
              <li>Transmit any viruses, malware, or other malicious code</li>
              <li>Impersonate any person or entity or misrepresent your affiliation</li>
              <li>Interfere with or disrupt the Services or servers</li>
              <li>Collect or harvest information about other users</li>
              <li>Use the Services in any way that could damage, disable, or impair the Services</li>
              <li>Attempt to gain unauthorized access to any portion of the Services</li>
              <li>Transfer, sell, or sublicense your account or access to the Services</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Loyalty Program and Coupons</h2>

            <h3>5.1 Program Participation</h3>
            <p>
              Access to the loyalty program and associated benefits is provided through your organization's partnership with Block Club. Program eligibility and benefits are subject to your organization's policies and our agreement with them.
            </p>

            <h3>5.2 Coupon Terms</h3>
            <p>
              Coupons and offers available through the Services are subject to specific terms and conditions, including:
            </p>
            <ul>
              <li>Expiration dates and redemption periods</li>
              <li>Single-use limitations unless otherwise specified</li>
              <li>Venue-specific restrictions and availability</li>
              <li>Non-transferability to other users</li>
              <li>Cannot be combined with other offers unless explicitly stated</li>
              <li>Subject to venue acceptance and availability</li>
            </ul>

            <h3>5.3 Redemption</h3>
            <p>
              To redeem a coupon, you must present the coupon through the Services at the participating venue. Venues reserve the right to refuse redemption if:
            </p>
            <ul>
              <li>The coupon has expired</li>
              <li>The coupon has already been redeemed</li>
              <li>The terms and conditions are not met</li>
              <li>There is suspicion of fraud or misuse</li>
            </ul>

            <h3>5.4 No Cash Value</h3>
            <p>
              Coupons have no cash value and cannot be exchanged for cash. Discounts apply only to eligible purchases and cannot exceed the purchase amount.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Intellectual Property Rights</h2>

            <h3>6.1 Our Property</h3>
            <p>
              The Services and all content, features, and functionality (including but not limited to all information, software, text, displays, images, video, audio, and the design, selection, and arrangement thereof) are owned by Block Club, its licensors, or other providers of such material and are protected by copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>

            <h3>6.2 Trademarks</h3>
            <p>
              Block Club and our logos, product or service names, and slogans are trademarks of Block Club and may not be copied, imitated, or used, in whole or in part, without our prior written permission. All other trademarks, registered trademarks, product names, and company names or logos mentioned in the Services are the property of their respective owners.
            </p>

            <h3>6.3 Feedback</h3>
            <p>
              If you provide us with any feedback, suggestions, or ideas about the Services ("Feedback"), you grant us a perpetual, irrevocable, worldwide, royalty-free license to use, modify, and incorporate such Feedback into our Services without any obligation to you.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Third-Party Services and Links</h2>
            <p>
              The Services may contain links to third-party websites, services, or venues that are not owned or controlled by Block Club. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party websites or services.
            </p>
            <p>
              You acknowledge and agree that Block Club shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection with the use of or reliance on any such content, goods, or services available on or through any such third-party websites or services.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Disclaimer of Warranties</h2>
            <p>
              THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.
            </p>
            <p>
              WE DO NOT WARRANT THAT:
            </p>
            <ul>
              <li>The Services will function uninterrupted, securely, or be available at any particular time or location</li>
              <li>Any errors or defects will be corrected</li>
              <li>The Services are free of viruses or other harmful components</li>
              <li>The results of using the Services will meet your requirements</li>
              <li>Any coupons or offers will be available or honored by participating venues</li>
            </ul>
            <p>
              YOUR USE OF THE SERVICES IS AT YOUR SOLE RISK. SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF IMPLIED WARRANTIES, SO SOME OF THE ABOVE EXCLUSIONS MAY NOT APPLY TO YOU.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Limitation of Liability</h2>
            <p>
              TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL BLOCK CLUB, ITS AFFILIATES, LICENSORS, SERVICE PROVIDERS, EMPLOYEES, AGENTS, OFFICERS, OR DIRECTORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION LOST PROFITS, LOST DATA, PERSONAL INJURY, OR PROPERTY DAMAGE ARISING OUT OF OR RELATED TO YOUR USE OR INABILITY TO USE THE SERVICES.
            </p>
            <p>
              OUR TOTAL LIABILITY TO YOU FOR ANY DAMAGES ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICES SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU HAVE PAID US IN THE PAST TWELVE MONTHS OR (B) ONE HUNDRED DOLLARS ($100).
            </p>
            <p>
              SOME JURISDICTIONS DO NOT ALLOW THE LIMITATION OR EXCLUSION OF LIABILITY FOR INCIDENTAL OR CONSEQUENTIAL DAMAGES, SO THE ABOVE LIMITATION OR EXCLUSION MAY NOT APPLY TO YOU.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Indemnification</h2>
            <p>
              You agree to defend, indemnify, and hold harmless Block Club and its affiliates, licensors, and service providers, and its and their respective officers, directors, employees, contractors, agents, licensors, suppliers, successors, and assigns from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to:
            </p>
            <ul>
              <li>Your violation of these Terms</li>
              <li>Your use of the Services</li>
              <li>Your violation of any rights of another party</li>
              <li>Your violation of any applicable laws or regulations</li>
              <li>Any fraudulent or illegal activity related to your account</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>11. Dispute Resolution</h2>

            <h3>11.1 Governing Law</h3>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the State of Missouri, United States, without regard to its conflict of law provisions.
            </p>

            <h3>11.2 Arbitration Agreement</h3>
            <p>
              Any dispute, claim, or controversy arising out of or relating to these Terms or the Services shall be resolved through binding arbitration administered by the American Arbitration Association (AAA) in accordance with its Commercial Arbitration Rules. The arbitration shall be conducted in St. Louis, Missouri.
            </p>

            <h3>11.3 Class Action Waiver</h3>
            <p>
              YOU AND BLOCK CLUB AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
            </p>

            <h3>11.4 Exceptions</h3>
            <p>
              Notwithstanding the foregoing, either party may bring an action in court to:
            </p>
            <ul>
              <li>Seek injunctive or other equitable relief</li>
              <li>Enforce intellectual property rights</li>
              <li>Resolve disputes related to the validity or enforceability of the arbitration agreement</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>12. Miscellaneous</h2>

            <h3>12.1 Entire Agreement</h3>
            <p>
              These Terms, together with our Privacy Policy and Cookie Policy, constitute the entire agreement between you and Block Club regarding the Services and supersede all prior agreements and understandings.
            </p>

            <h3>12.2 Severability</h3>
            <p>
              If any provision of these Terms is found to be unlawful, void, or unenforceable, that provision shall be deemed severable and shall not affect the validity and enforceability of the remaining provisions.
            </p>

            <h3>12.3 Waiver</h3>
            <p>
              Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. Any waiver of any provision of these Terms will be effective only if in writing and signed by Block Club.
            </p>

            <h3>12.4 Assignment</h3>
            <p>
              You may not assign or transfer these Terms or your rights hereunder without our prior written consent. We may assign these Terms or any rights hereunder without your consent.
            </p>

            <h3>12.5 Force Majeure</h3>
            <p>
              Block Club shall not be liable for any failure or delay in performance due to causes beyond our reasonable control, including but not limited to acts of God, war, strikes, labor disputes, embargoes, government orders, or natural disasters.
            </p>

            <h3>12.6 Survival</h3>
            <p>
              The following sections shall survive termination of these Terms: Intellectual Property Rights, Disclaimer of Warranties, Limitation of Liability, Indemnification, Dispute Resolution, and Miscellaneous.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Contact Information</h2>
            <p>
              If you have any questions about these Terms, please contact us:
            </p>
            <div className="contact-info">
              <p><strong>Email:</strong> support@blockclub.com</p>
              <p><strong>Address:</strong> Block Club, St. Louis, MO</p>
              <p><strong>Legal Department:</strong> legal@blockclub.com</p>
            </div>
          </section>

          <section className="legal-section">
            <h2>14. Acknowledgment</h2>
            <p>
              BY USING THE SERVICES, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS OF SERVICE AND AGREE TO BE BOUND BY THEM.
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default TermsOfService;
